<template>
  <!-- The options are the control: each one is a radio (single) or a checkbox
       (multi), so the wrapper is the corresponding grouping role and every
       option carries its own checked state. This is independent of the author's
       optional onClick processor — clicking always changes the selection. -->
  <div class="option-select-base pa-items" :role="isMultiSelect ? 'group' : 'radiogroup'">
    <div
      class="option pa-item"
      v-for="(option, index) in optionList"
      :key="option.id"
      :data-part="option.id"
      :style="{ '--pa-i': index }"
      :ref="(el) => setOptionRef(el, index)"
      :role="isMultiSelect ? 'checkbox' : 'radio'"
      :aria-checked="isSelected(option.value)"
      :aria-disabled="isDisabled(option) || undefined"
      :tabindex="optionTabIndex(index)"
      :class="[
        { active: isSelected(option.value) },
        { 'has-image': showImage },
        { [`image-${imagePosition}`]: showImage || showPadImage },
        { [`image-mode-${imageMode}`]: showImage },
        { 'has-pad-image': showPadImage && !!fields.padImage?.id },
        { 'subtext-row-mode': isSubtextRowMode },
        { 'no-select': isNoSelect },
        { 'is-disabled': isDisabled(option) },
      ]"
      @click="clickOption(option)"
      @keydown="onOptionKeydown($event, option, index)"
    >
      <div class="option-main-row">
        <span
          v-if="showIndicator"
          :class="[
            'indicator',
            {
              checked: isSelected(option.value),
              checkbox: isMultiSelect,
              radio: !isMultiSelect,
            },
          ]"
          :style="{ order: indicatorOrder }"
        >
          <svg
            v-if="isMultiSelect"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 12.3636L9.75 16L18 8"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
          <span v-else class="radio-dot" />
        </span>

        <div class="text" :style="{ order: labelOrder }">
          <TipTap
            :model-value="pipe.getTranslation(option.label.key)"
            @update:model-value="(val) => pipe.setTranslation(option.label.key, val as object)"
            mode="single"
          />
          <div v-if="!isSubtextRowMode && option.subtext?.key" class="subtext">
            <TipTap
              :model-value="pipe.getTranslation(option.subtext.key)"
              @update:model-value="(val) => pipe.setTranslation(option.subtext!.key, val as object)"
              mode="single"
            />
          </div>
        </div>

        <div v-if="showPadImage && fields.padImage?.id" class="option-image-pad">
          <PlainImage
            :image="fields.padImage as any"
            :skeleton-color="imageSkeletonBgColor"
            :shimmer-color="imageSkeletonShimmerColor"
            class="img"
          />
        </div>

        <div
          v-if="showImage && imagePosition !== Position.BOTH && primaryImage(option)?.id"
          class="option-image"
          :style="optionImageStyle(option)"
        >
          <PlainImage
            :image="primaryImage(option) as any"
            :skeleton-color="imageSkeletonBgColor"
            :shimmer-color="imageSkeletonShimmerColor"
            class="img"
          />
        </div>

        <div
          v-if="showImage && imagePosition === Position.BOTH && primaryImage(option)?.id"
          class="option-image option-image-left"
          :style="optionImageStyleBoth(option, 'left')"
        >
          <PlainImage
            :image="primaryImage(option) as any"
            :skeleton-color="imageSkeletonBgColor"
            :shimmer-color="imageSkeletonShimmerColor"
            class="img"
          />
        </div>

        <div
          v-if="showImage && imagePosition === Position.BOTH && option.imageSecondary?.id"
          class="option-image option-image-right"
          :style="optionImageStyleBoth(option, 'right')"
        >
          <PlainImage
            :image="option.imageSecondary as any"
            :skeleton-color="imageSkeletonBgColor"
            :shimmer-color="imageSkeletonShimmerColor"
            class="img"
          />
        </div>
      </div>

      <div
        v-if="isSubtextRowMode && option.subtext?.key"
        class="option-subtext-row-wrapper"
        :class="{ 'is-expanded': isRowExpanded(option.value) }"
      >
        <div class="option-subtext-row-inner">
          <div class="option-subtext-row">
            <TipTap
              :model-value="pipe.getTranslation(option.subtext.key)"
              @update:model-value="(val) => pipe.setTranslation(option.subtext!.key, val as object)"
              mode="single"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TipTap from '@render/lib/TipTap'
import { type OptionSelectOption } from '@api/types.gen'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useDirection } from '@render/composables/useDirection'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingSidesStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { computed, ref, watch, type ComponentPublicInstance } from 'vue'
import {
  type OptionSelectFields,
  getDefaultOptionSelectFields,
  OnSelectBehavior,
  Position,
  ImageMode,
  SubtextMode,
} from './types'
import { nextMultiSelection } from './multiSelection'
import PlainImage from '../../generic/PlainImage.vue'

const props = defineProps<{
  isMultiSelect: boolean
}>()

const emit = defineEmits<{
  (e: 'onClick'): void
}>()

const fields = defineModel<OptionSelectFields>('fields', {
  required: true,
  default: () => getDefaultOptionSelectFields(),
})

const pipe = usePipe()
const { isRtl } = useDirection()

// One source for the rendered list — the v-for, the roving tabindex and the
// arrow-key walk all index into the same array.
const optionList = computed(() => fields.value.options?.options ?? [])

const showIndicator = computed(() => fields.value.showIndicator !== false)
const indicatorPosition = computed(() => fields.value.indicatorPosition || Position.LEFT)
const indicatorOrder = computed(() => (indicatorPosition.value === Position.RIGHT ? 2 : 0))

const showImage = computed(() => fields.value.showImage === true)
const imagePosition = computed(() => fields.value.imagePosition || Position.LEFT)
const imageMode = computed(() => fields.value.imageMode || ImageMode.INLINE)

// Under RTL the images are horizontally mirrored (scaleX(-1)) by default — for
// directional graphics. Each can opt out independently (negative-form flag →
// omitted/false = mirrored). The card layout still mirrors the image's SIDE via
// the flex order + logical CSS regardless of these flags.
const optionImageMirror = computed(() =>
  isRtl.value && fields.value.doNotMirrorOptionImageOnRtl !== true ? 'scaleX(-1)' : 'none',
)
const padImageMirror = computed(() =>
  isRtl.value && fields.value.doNotMirrorPadImageOnRtl !== true ? 'scaleX(-1)' : 'none',
)

const subtextMode = computed(() => fields.value.subtextMode || SubtextMode.REGULAR)
const isSubtextRowMode = computed(() => subtextMode.value === SubtextMode.NEW_ROW)
const isCollapsible = computed(() => fields.value.isCollapsible === true)

const isNoSelect = computed(() => {
  const mode = pipe.currentMode.value
  return mode === PipeDisplayMode.STATIC
})

const imageOrder = computed(() => {
  const indPos = indicatorPosition.value
  const imgPos = imagePosition.value
  if (imgPos === Position.LEFT) {
    return indPos === Position.LEFT ? 1 : 0
  }

  return indPos === Position.RIGHT ? 1 : 2
})

const labelOrder = computed(() => {
  const indPos = indicatorPosition.value
  const imgPos = imagePosition.value
  if (!showImage.value) return indPos === Position.LEFT ? 1 : 0
  // With image: label is always in the middle unless both indicator and image are on the same side
  if (imgPos === Position.LEFT && indPos === Position.LEFT) return 2
  if (imgPos === Position.RIGHT && indPos === Position.RIGHT) return 0

  return 1
})

const imageHeightDefaults: Record<string, string> = {
  [ImageMode.INLINE]: '40px',
  [ImageMode.EDGE]: '86px',
}

const imageHeight = computed(() => {
  if (fields.value.imageHeight) {
    return (
      getSizeStyleData(fields.value.imageHeight) ?? imageHeightDefaults[imageMode.value] ?? '40px'
    )
  }

  return imageHeightDefaults[imageMode.value] ?? '40px'
})

const imageOverflowOffset = computed(() => {
  if (fields.value.imageOverflowOffset) {
    return getSizeStyleData(fields.value.imageOverflowOffset) ?? '24px'
  }

  return '24px'
})

// Image skeleton colors
const imageSkeletonBgColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonBgColor) ?? 'transparent',
)
const imageSkeletonShimmerColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)

// Style overrides — defaults are semantic vars from the theme stylesheet
const overrideBgColor = computed(
  () => getColorStyleData(fields.value.bgColor) ?? 'var(--color-action-tertiary-background)',
)
const overrideActiveBgColor = computed(
  () =>
    getColorStyleData(fields.value.activeBgColor) ??
    'var(--color-action-tertiary-background-pressed)',
)
const overrideBorderColor = computed(
  () => getColorStyleData(fields.value.borderColor) ?? 'var(--color-action-tertiary-border)',
)
const overrideActiveBorderColor = computed(
  () =>
    getColorStyleData(fields.value.activeBorderColor) ??
    'var(--color-action-tertiary-border-pressed)',
)

// Text
const overrideTextColor = computed(
  () => getColorStyleData(fields.value.textColor) ?? 'var(--color-action-tertiary-text)',
)
const overrideTextHoverColor = computed(
  () => getColorStyleData(fields.value.textHoverColor) ?? overrideTextColor.value,
)
const overrideTextActiveColor = computed(
  () => getColorStyleData(fields.value.textActiveColor) ?? overrideTextColor.value,
)
const overrideTextFontSize = computed(
  () => getSizeStyleData(fields.value.textFontSize) ?? 'var(--text-body-lg-size)',
)
const overrideTextFontWeight = computed(
  () => fields.value.textFontWeight || 'var(--text-body-lg-weight)',
)
const overrideTextLineHeight = computed(
  () => getSizeStyleData(fields.value.textLineHeight) ?? 'var(--text-body-lg-line-height)',
)

// Subtext
const overrideSubtextColor = computed(
  () => getColorStyleData(fields.value.subtextColor) ?? 'var(--color-action-tertiary-text)',
)
const overrideSubtextHoverColor = computed(
  () => getColorStyleData(fields.value.subtextHoverColor) ?? overrideSubtextColor.value,
)
const overrideSubtextActiveColor = computed(
  () => getColorStyleData(fields.value.subtextActiveColor) ?? overrideSubtextColor.value,
)
const overrideSubtextFontSize = computed(
  () => getSizeStyleData(fields.value.subtextFontSize) ?? 'var(--text-body-sm-size)',
)
const overrideSubtextFontWeight = computed(
  () => fields.value.subtextFontWeight || 'var(--text-body-sm-weight)',
)
const overrideSubtextLineHeight = computed(
  () => getSizeStyleData(fields.value.subtextLineHeight) ?? 'var(--text-body-sm-line-height)',
)
const overrideSubtextGap = computed(
  () => getSizeStyleData(fields.value.subtextGap) ?? 'var(--space-8)',
)

// Radio indicator (single-select)
const overrideRadioBgIdle = computed(
  () => getColorStyleData(fields.value.radioBgIdle) ?? 'var(--color-background-subtle-accent)',
)
const overrideRadioBgHover = computed(
  () => getColorStyleData(fields.value.radioBgHover) ?? 'var(--color-background-subtle-accent)',
)
const overrideRadioBgSelected = computed(
  () => getColorStyleData(fields.value.radioBgSelected) ?? 'var(--color-brand-secondary-subtle)',
)
const overrideRadioBorderIdle = computed(
  () => getColorStyleData(fields.value.radioBorderIdle) ?? 'var(--color-background-subtle-accent)',
)
const overrideRadioBorderHover = computed(
  () => getColorStyleData(fields.value.radioBorderHover) ?? 'var(--color-background-subtle-accent)',
)
const overrideRadioBorderSelected = computed(
  () => getColorStyleData(fields.value.radioBorderSelected) ?? 'var(--color-border-accent)',
)
const overrideRadioFillIdle = computed(
  () => getColorStyleData(fields.value.radioFillIdle) ?? 'transparent',
)
const overrideRadioFillHover = computed(
  () => getColorStyleData(fields.value.radioFillHover) ?? 'transparent',
)
const overrideRadioFillSelected = computed(
  () => getColorStyleData(fields.value.radioFillSelected) ?? 'var(--color-brand-secondary)',
)

// Checkbox indicator (multi-select)
const overrideCheckboxBgIdle = computed(
  () => getColorStyleData(fields.value.checkboxBgIdle) ?? 'var(--color-background-subtle)',
)
const overrideCheckboxBgHover = computed(
  () => getColorStyleData(fields.value.checkboxBgHover) ?? 'var(--color-background-subtle)',
)
const overrideCheckboxBgSelected = computed(
  () => getColorStyleData(fields.value.checkboxBgSelected) ?? 'var(--color-brand-secondary)',
)
const overrideCheckboxBorderIdle = computed(
  () => getColorStyleData(fields.value.checkboxBorderIdle) ?? 'var(--color-action-tertiary-border)',
)
const overrideCheckboxBorderHover = computed(
  () =>
    getColorStyleData(fields.value.checkboxBorderHover) ??
    'var(--color-action-tertiary-border-pressed)',
)
const overrideCheckboxBorderSelected = computed(
  () =>
    getColorStyleData(fields.value.checkboxBorderSelected) ??
    'var(--color-action-tertiary-border-pressed)',
)
const overrideCheckboxFillIdle = computed(
  () => getColorStyleData(fields.value.checkboxFillIdle) ?? 'transparent',
)
const overrideCheckboxFillHover = computed(
  () => getColorStyleData(fields.value.checkboxFillHover) ?? 'transparent',
)
const overrideCheckboxFillSelected = computed(
  () => getColorStyleData(fields.value.checkboxFillSelected) ?? 'var(--color-icon-inverse)',
)

const overrideGap = computed(() => getSizeStyleData(fields.value.gap) ?? 'var(--space-16)')
const overrideGapColumns = computed(
  () => getSizeStyleData(fields.value.gapColumns) ?? 'var(--space-16)',
)
const overridePadding = computed(() => {
  const sides = getSpacingSidesStyleData(fields.value.padding)
  if (sides) return sides
  const y = getSizeStyleData(fields.value.paddingY) ?? 'var(--space-16)'
  const x = getSizeStyleData(fields.value.paddingX) ?? 'var(--space-16)'
  return { top: y, end: x, bottom: y, start: x }
})
const overridePaddingShorthand = computed(() => {
  const { top, end, bottom, start } = overridePadding.value
  return isRtl.value ? `${top} ${start} ${bottom} ${end}` : `${top} ${end} ${bottom} ${start}`
})
const overridePaddingTop = computed(() => overridePadding.value.top)
const overridePaddingEnd = computed(() => overridePadding.value.end)
const overridePaddingBottom = computed(() => overridePadding.value.bottom)
const overridePaddingStart = computed(() => overridePadding.value.start)
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight) ?? '78px')
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '480px')
const overrideBorderRadius = computed(
  () => getSizeStyleData(fields.value.borderRadius) ?? 'var(--radius-xs)',
)
const overrideMargin = computed(() => getSpacingStyleData(fields.value.margin) ?? '0')

const selection = computed({
  get() {
    const key = fields.value.valueVariable?.key ?? ''
    const raw = pipe.getData(`var.${key}`)

    if (props.isMultiSelect) {
      return Array.isArray(raw) ? (raw as string[]) : []
    }

    return (raw ?? '') as string
  },
  set(val) {
    const key = fields.value.valueVariable?.key ?? ''
    pipe.setData(`var.${key}`, val)
  },
})

const showPadImage = computed(() => fields.value.showPadImage === true)

watch(
  () => [fields.value.hasSelectionVariable?.key, selection.value] as const,
  ([key, sel]) => {
    if (!key) return
    const hasSelection = props.isMultiSelect
      ? Array.isArray(sel) && sel.length > 0
      : typeof sel === 'string' && sel !== ''
    pipe.setData(`var.${key}`, hasSelection)
  },
  { immediate: true },
)

const isSelected = (optionValue: string) =>
  props.isMultiSelect
    ? (selection.value as string[]).includes(optionValue)
    : selection.value !== '' && selection.value === optionValue

// Multi-select only: true once the selection has reached the configured limit
const isSelectionLimit = computed(() => {
  const max = fields.value.maxSelections
  return (
    props.isMultiSelect && max != null && max > 0 && (selection.value as string[]).length >= max
  )
})

// Dim the options that can no longer be selected due to limit.
// DESELECT_ALL ("None…") and SELECT_ALL stay fully interactive
// so the user can always clear/replace their selection.
const isDisabled = (option: OptionSelectOption) =>
  isSelectionLimit.value &&
  !isSelected(option.value) &&
  (option.onSelectBehavior ?? OnSelectBehavior.NONE) === OnSelectBehavior.NONE
// Backward-compat read: pre-rename data has `image`, new data has `imagePrimary`.
const primaryImage = (option: OptionSelectOption) =>
  option.imagePrimary ?? (option as unknown as { image?: typeof option.imagePrimary }).image

// Single source of truth for .option-image container's dynamic styles.
// min-width reserves horizontal space pre-load so the label doesn't fill the
// row and shift once the image arrives. Aspect uses intrinsic width/height when
// available, falling back to 1 (square) for legacy fields and EDGE mode, where
// height is auto and width can't be derived. min(..., 112px) mirrors the CSS
// max-width so very wide intrinsics stay within the column cap.
const optionImageStyle = (option: OptionSelectOption) => {
  const img = primaryImage(option) as { width?: number; height?: number } | undefined
  const aspect = img?.width && img?.height ? img.width / img.height : 1
  return {
    order: imageOrder.value,
    minWidth: `min(calc(${imageHeight.value} * ${aspect}), 112px)`,
  }
}

// In 'both' mode the primary image uses order -1 (before all flex items) and the
// secondary image uses order 100 (after all flex items), sandwiching the indicator
// and label without touching their existing order computeds.
const optionImageStyleBoth = (option: OptionSelectOption, side: 'left' | 'right') => {
  const raw =
    side === 'right'
      ? (option.imageSecondary as { width?: number; height?: number } | undefined)
      : (primaryImage(option) as { width?: number; height?: number } | undefined)
  const aspect = raw?.width && raw?.height ? raw.width / raw.height : 1

  return {
    order: side === 'left' ? 0 : 100,
    minWidth: `min(calc(${imageHeight.value} * ${aspect}), 112px)`,
  }
}

const isRowExpanded = (optionValue: string) =>
  isSubtextRowMode.value && (!isCollapsible.value || isSelected(optionValue))

const clickOption = (option: OptionSelectOption) => {
  if (props.isMultiSelect) {
    const next = nextMultiSelection({
      options: fields.value.options?.options ?? [],
      selection: selection.value as string[],
      clicked: option,
      behavior: (option.onSelectBehavior ?? OnSelectBehavior.NONE) as OnSelectBehavior,
      maxSelections: fields.value.maxSelections,
    })

    // A click blocked by the selection limit changes nothing — onClick is
    // skipped too.
    if (next === null) return

    selection.value = next
  } else {
    selection.value = option.value
  }
  emit('onClick')
}

// --- Keyboard -------------------------------------------------------------
// Enter / Space activate; the arrow keys walk the list. Every handler below is
// entered only when the OPTION ITSELF has focus — a keystroke on a descendant
// (the TipTap label editor in the builder) belongs to that descendant, and
// `preventDefault()` here would swallow the author's spaces, newlines and caret
// movement.

const optionEls = ref<(HTMLElement | null)[]>([])

const setOptionRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  optionEls.value[index] = (el as HTMLElement | null) ?? null
}

const selectedIndex = computed(() => optionList.value.findIndex((o) => isSelected(o.value)))

// Roving tabindex, radio-group style: Tab reaches the group once — landing on
// the checked option, or the first when nothing is checked — and the arrow keys
// move within it. A checkbox group keeps every option individually tabbable,
// which is what the checkbox pattern expects.
const optionTabIndex = (index: number): number => {
  if (props.isMultiSelect) return 0
  return index === Math.max(selectedIndex.value, 0) ? 0 : -1
}

type ArrowMove = 1 | -1 | 'first' | 'last' | null

const arrowMoveForKey = (key: string): ArrowMove => {
  switch (key) {
    case 'ArrowDown':
      return 1
    case 'ArrowUp':
      return -1
    // Horizontal arrows follow the writing direction, so "next" stays "further
    // along the list" in an RTL language.
    case 'ArrowRight':
      return isRtl.value ? -1 : 1
    case 'ArrowLeft':
      return isRtl.value ? 1 : -1
    case 'Home':
      return 'first'
    case 'End':
      return 'last'
    default:
      return null
  }
}

const moveToOption = (index: number) => {
  const option = optionList.value[index]
  if (!option) return
  // Single-select follows the radio pattern: moving focus moves the selection.
  // It deliberately does NOT emit `onClick` — an author's processors routinely
  // navigate to the next page, which would make arrowing through the list
  // impossible. Enter / Space still runs the full click path, processors and
  // all. A checkbox group never selects on arrow: passing over an option would
  // otherwise toggle it.
  if (!props.isMultiSelect) selection.value = option.value
  optionEls.value[index]?.focus()
}

const onOptionKeydown = (event: KeyboardEvent, option: OptionSelectOption, index: number) => {
  if (event.target !== event.currentTarget) return

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    clickOption(option)
    return
  }

  const move = arrowMoveForKey(event.key)
  if (move === null) return
  const count = optionList.value.length
  if (count === 0) return
  // Swallow the key so the arrows don't also scroll the page.
  event.preventDefault()

  if (move === 'first') return moveToOption(0)
  if (move === 'last') return moveToOption(count - 1)
  // Wrap, as a radio group does.
  moveToOption((index + move + count) % count)
}
</script>

<style scoped lang="scss">
// The card chrome and indicator blocks below are duplicated in
// OptionSelectItem.vue (the single standalone item). Change both together.
//
// The hover treatment, shared with `:focus-visible` — see the two call sites in
// the interaction-states block below.
@mixin option-interactive {
  border-color: v-bind(overrideActiveBorderColor);
  box-shadow: var(--shadow-inner-input-hover);

  .text {
    color: v-bind(overrideTextHoverColor);

    .subtext {
      color: v-bind(overrideSubtextHoverColor);
    }
  }

  // Radio
  .indicator.radio:not(.checked) {
    background-color: v-bind(overrideRadioBgHover);
    border-color: v-bind(overrideRadioBorderHover);

    .radio-dot {
      background-color: v-bind(overrideRadioFillHover);
    }
  }

  // Checkbox
  .indicator.checkbox:not(.checked) {
    background-color: v-bind(overrideCheckboxBgHover);
    border-color: v-bind(overrideCheckboxBorderHover);

    svg {
      stroke: v-bind(overrideCheckboxFillHover);
    }
  }
}

.option-select-base {
  display: flex;
  flex-direction: column;
  gap: v-bind(overrideGap);
  margin: v-bind(overrideMargin);
  max-width: v-bind(overrideMaxWidth);

  > .option {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    min-height: v-bind(overrideMinHeight);
    padding: v-bind(overridePaddingShorthand);
    cursor: pointer;
    background-color: v-bind(overrideBgColor);
    border-width: var(--border-width);
    border-style: solid;
    border-color: v-bind(overrideBorderColor);
    border-radius: v-bind(overrideBorderRadius);
    transition: all 0.2s ease;

    .option-main-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: v-bind(overrideGapColumns);
      width: 100%;
    }

    .option-subtext-row-wrapper {
      z-index: 3;
      display: grid;
      grid-template-rows: 1fr;
      transition: grid-template-rows 0.2s ease;

      &:not(.is-expanded) {
        grid-template-rows: 0fr;
      }

      .option-subtext-row-inner {
        overflow: hidden;
        min-height: 0;
      }

      .option-subtext-row {
        padding-top: v-bind(overrideSubtextGap);
        font-family: var(--text-body-sm-family);
        font-weight: v-bind(overrideSubtextFontWeight);
        font-size: v-bind(overrideSubtextFontSize);
        line-height: v-bind(overrideSubtextLineHeight);
        color: v-bind(overrideSubtextColor);
        transition: color 0.2s ease;
      }
    }

    &.active .option-subtext-row {
      color: v-bind(overrideSubtextActiveColor);
    }

    // Gate hover styling behind a real hover-capable pointer. On touch devices the
    // browser emulates `:hover` on tap and the state "sticks" until the next pointer
    // interaction, so an option looks highlighted after a tap-driven page navigation.
    // `@media (hover: hover)` applies these styles only to mouse-like pointers (and the
    // editor preview); touch keeps the `:active` tap feedback below.
    @media (hover: hover) {
      &:hover .option-subtext-row {
        color: v-bind(overrideSubtextHoverColor);
      }
    }

    &.active {
      border-color: v-bind(overrideActiveBorderColor);
      background-color: v-bind(overrideActiveBgColor);

      .text {
        color: v-bind(overrideTextActiveColor);

        .subtext {
          color: v-bind(overrideSubtextActiveColor);
        }
      }
    }

    &.no-select {
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .text {
      flex: 1;
      min-width: 0;
      text-align: start;
      color: v-bind(overrideTextColor);
      font-family: var(--text-body-lg-family);
      font-weight: v-bind(overrideTextFontWeight);
      font-size: v-bind(overrideTextFontSize);
      line-height: v-bind(overrideTextLineHeight);
      z-index: 3;
      transition: color 0.2s ease;

      .subtext {
        font-family: var(--text-body-sm-family);
        font-weight: v-bind(overrideSubtextFontWeight);
        font-size: v-bind(overrideSubtextFontSize);
        line-height: v-bind(overrideSubtextLineHeight);
        color: v-bind(overrideSubtextColor);
        margin-top: v-bind(overrideSubtextGap);
        transition: color 0.2s ease;
      }
    }

    .indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 1.5rem;
      width: 1.5rem;
      flex-shrink: 0;
      border-width: var(--border-width);
      border-style: solid;
      transition:
        background-color 0.2s,
        border-color 0.2s;
      z-index: 4;
    }

    // Radio — idle state
    .indicator.radio {
      border-radius: 50%;
      background-color: v-bind(overrideRadioBgIdle);
      border-color: v-bind(overrideRadioBorderIdle);

      .radio-dot {
        width: 0.75rem;
        height: 0.75rem;
        border-radius: 50%;
        background-color: v-bind(overrideRadioFillIdle);
        transform: scale(0);
        transition: all 0.2s ease;
      }

      // Radio — selected state
      &.checked {
        background-color: v-bind(overrideRadioBgSelected);
        border-color: v-bind(overrideRadioBorderSelected);

        .radio-dot {
          background-color: v-bind(overrideRadioFillSelected);
          transform: scale(1);
        }
      }
    }

    // Checkbox — idle state
    .indicator.checkbox {
      border-radius: var(--radius-xs);
      background-color: v-bind(overrideCheckboxBgIdle);
      border-color: v-bind(overrideCheckboxBorderIdle);

      svg {
        opacity: 0;
        transform: scale(0.5);
        transition: all 0.2s ease;
        stroke: v-bind(overrideCheckboxFillIdle);
      }

      // Checkbox — selected state
      &.checked {
        background-color: v-bind(overrideCheckboxBgSelected);
        border-color: v-bind(overrideCheckboxBorderSelected);

        svg {
          opacity: 1;
          transform: scale(1);
          stroke: v-bind(overrideCheckboxFillSelected);
        }
      }
    }

    //
    // With image
    //
    .option-image {
      flex-shrink: 0;
      height: v-bind(imageHeight);
      max-width: 112px;
      // `min-width` is set inline per-option by `optionImageStyle()` based on
      // the image's intrinsic aspect (or a square fallback when dims are
      // missing). That's the single source of truth for sizing this container.

      .img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        // RTL: mirror the graphic by default (opt out via doNotMirrorOptionImageOnRtl).
        transform: v-bind(optionImageMirror);
      }
    }

    //
    // Edge mode: stretch to card border via negative margins
    //
    &.image-mode-edge .option-image {
      align-self: stretch;
      overflow: hidden;
      height: auto;
      min-height: v-bind(imageHeight);
      margin-top: calc(-1 * v-bind(overridePaddingTop));
      margin-bottom: calc(-1 * v-bind(overridePaddingBottom));
    }

    &.image-mode-edge.image-right .option-image {
      margin-inline-end: calc(-1 * v-bind(overridePaddingEnd));
      border-start-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    &.image-mode-edge.image-left .option-image {
      margin-inline-start: calc(-1 * v-bind(overridePaddingStart));
      border-start-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    //
    // Overflow mode: absolute positioning, extends above card
    //
    &.image-mode-overflow {
      position: relative;
      overflow: visible;
    }

    &.image-mode-overflow .option-image {
      position: absolute;
      top: calc(-1 * v-bind(imageOverflowOffset));
      bottom: 0;
      z-index: 2;
      height: auto;

      .img {
        object-position: bottom;
      }
    }

    &.image-mode-overflow.image-right {
      padding-inline-end: calc(112px + v-bind(overridePaddingEnd));
      .option-image {
        inset-inline-end: 0;
      }
    }

    &.image-mode-overflow.image-left {
      padding-inline-start: calc(112px + v-bind(overridePaddingStart));
      .option-image {
        inset-inline-start: 0;
      }
    }

    //
    // Pad image (decorative background behind main image)
    //
    &.has-pad-image {
      position: relative;
    }

    .option-image-pad {
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 1;
      width: 112px;
      overflow: hidden;

      .img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: left;
        // RTL: mirror the graphic by default (opt out via doNotMirrorPadImageOnRtl).
        transform: v-bind(padImageMirror);

        // object-position has no logical keyword; mirror the focal edge in RTL.
        [dir='rtl'] & {
          object-position: right;
        }
      }
    }

    &.image-right .option-image-pad {
      inset-inline-end: 0;
      border-start-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    &.image-left .option-image-pad {
      inset-inline-start: 0;
      border-start-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    // Main image above pad
    &.has-pad-image .option-image {
      z-index: 2;
    }

    //
    // Both mode: image on left AND right
    //
    &.image-mode-edge.image-both .option-image-left {
      margin-inline-start: calc(-1 * v-bind(overridePaddingStart));
      border-start-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    &.image-mode-edge.image-both .option-image-right {
      margin-inline-end: calc(-1 * v-bind(overridePaddingEnd));
      border-start-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-end-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    &.image-mode-overflow.image-both {
      padding-inline-start: calc(112px + v-bind(overridePaddingStart));
      padding-inline-end: calc(112px + v-bind(overridePaddingEnd));

      .option-image-left {
        inset-inline-start: 0;
      }

      .option-image-right {
        inset-inline-end: 0;
      }
    }

    &.image-both .option-image-pad {
      inset-inline-start: 0;
      border-start-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
      border-end-start-radius: calc(v-bind(overrideBorderRadius) - var(--border-width));
    }

    //
    // Subtext on a new row mode.
    // Could be collapsible, so min height is ignored to avoid layout shifts.
    //
    &.subtext-row-mode {
      min-height: 0;
    }

    //
    // Interaction states
    //
    @media (hover: hover) {
      &:hover {
        @include option-interactive;
      }
    }

    // The option is keyboard-focusable (role=radio/checkbox + tabindex), so it
    // needs a visible state — it reuses the hover treatment rather than adding
    // an outline of its own. Unconditional: `:focus-visible` never matches a
    // tap, so it does not want the hover-capability gate above.
    &:focus-visible {
      outline: none;
      @include option-interactive;
    }

    &:active {
      box-shadow: var(--shadow-inner-input-hover);
    }
  }
}
</style>
